/**
 * OmniLeagues — Registration ▸ Payments (tenant) + the Semprion HQ payment cards.
 *
 * Tenant (Tech Specialist edits, AD read-only): how this organization takes money —
 *   • Semprion Payments (default): a Stripe Express account under Semprion HQ's
 *     Stripe platform; Stripe-hosted signup, payouts to the org's own bank.
 *   • Own Stripe (OAuth "Connect with Stripe") or own Square — override.
 *   Card and/or ACH bank debit, statement descriptor, online fee.
 * HQ (exported for /admin ▸ Billing ▸ Payment Settings): platform Stripe keys,
 * Semprion Payments fee + methods + bank feeds, Square platform keys, and every
 * org's payment setup. Keys are write-only; nothing secret reaches the browser.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { CreditCard, Link2, Unlink, ExternalLink, Loader2, AlertTriangle, CheckCircle2, RefreshCw, Copy, Trash2, Landmark } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_FIELDS = [
  ['secret_key', 'Secret key', 'sk_ / rk_'],
  ['publishable_key', 'Publishable key', 'pk_'],
  ['webhook_secret', 'Webhook signing secret (Your account)', 'whsec_'],
  ['connect_webhook_secret', 'Webhook signing secret (Connected accounts)', 'whsec_'],
  ['connect_client_id', 'Connect Client ID (only for orgs using their own Stripe)', 'ca_'],
];

const PROCESSORS = [
  ['semprion', 'Semprion Payments (recommended)'],
  ['stripe', 'Our own Stripe account'],
  ['square', 'Our own Square account'],
];

const money = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;
const feeText = (hq) => {
  if (!hq) return '';
  const parts = [];
  if (hq.fee_percent) parts.push(`${hq.fee_percent}%`);
  if (hq.fee_flat_cents) parts.push(money(hq.fee_flat_cents));
  return parts.length ? parts.join(' + ') : 'none';
};

// Semprion HQ: the platform Stripe keys (apply to EVERY org).
export function PlatformSetupCard({ isDark, card, inp }) {
  const [plat, setPlat] = useState(null);
  const [mode, setMode] = useState('test');
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [testRes, setTestRes] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/api/stripe-connect/platform`, { withCredentials: true })
      .then(r => { setPlat(r.data); if (r.data.mode) setMode(r.data.mode); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!plat) return null;

  const WARN = 'These platform keys apply to every organization on OmniLeagues.';
  const Status = () => (
    <span className="inline-flex items-center gap-1 text-xs" data-testid="platform-configured">
      {plat.configured ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}
      <span className={plat.configured ? 'text-green-500' : 'text-amber-500'}>Platform: {plat.configured ? 'configured' : 'not configured'}</span>
    </span>
  );

  if (!plat.can_edit) {
    return (
      <Card className={card} data-testid="platform-setup-readonly">
        <CardContent className="p-4 flex items-center gap-3">
          <Status />
          <span className={`text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{WARN}</span>
        </CardContent>
      </Card>
    );
  }

  const saved = (plat.fields || {})[mode] || {};
  const setup = plat.setup || {};
  const save = async () => {
    setBusy(true);
    try {
      const payload = { mode, [mode]: {} };
      PLATFORM_FIELDS.forEach(([f]) => { if ((vals[f] || '').trim()) payload[mode][f] = vals[f].trim(); });
      await axios.put(`${API}/api/stripe-connect/platform`, payload, { withCredentials: true });
      toast.success('Platform config saved'); setVals({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };
  const clearField = async (f) => {
    if (!window.confirm(`Clear the ${mode} ${f.replace(/_/g, ' ')}?`)) return;
    try { await axios.post(`${API}/api/stripe-connect/platform/clear`, { mode, field: f }, { withCredentials: true }); toast.success('Cleared'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Clear failed'); }
  };
  const testConn = async () => {
    setBusy(true); setTestRes(null);
    try { const { data } = await axios.post(`${API}/api/stripe-connect/platform/test-connection`, { mode }, { withCredentials: true }); setTestRes(data); }
    catch (e) { setTestRes({ ok: false, error: e.response?.data?.detail || 'failed' }); } finally { setBusy(false); }
  };
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success('Copied'); };
  const ModeBtn = ({ m, label }) => (
    <button onClick={() => setMode(m)} data-testid={`platform-mode-${m}`}
      className={`px-3 py-1 text-xs rounded-md border ${mode === m ? 'bg-indigo-600 text-white border-indigo-600' : (isDark ? 'border-slate-600 text-slate-300' : 'border-slate-300 text-slate-600')}`}>{label}</button>
  );
  const CopyRow = ({ label, value, testid }) => (
    <div className="flex items-center gap-2">
      <div className="flex-1 min-w-0">
        <span className="text-[10px] uppercase text-slate-500 block">{label}</span>
        <span className={`text-xs font-mono truncate block ${isDark ? 'text-slate-200' : 'text-slate-700'}`} data-testid={testid}>{value || '—'}</span>
      </div>
      {value && <button onClick={() => copy(value)} className="text-slate-400 hover:text-indigo-400" data-testid={`${testid}-copy`}><Copy className="h-3.5 w-3.5" /></button>}
    </div>
  );

  return (
    <Card className={card} data-testid="platform-setup-card">
      <CardHeader className="pb-2">
        <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
          <CreditCard className="h-4 w-4 text-indigo-400" /> Semprion Stripe account (platform) <Status />
          <span className="ml-auto flex gap-1">
            <ModeBtn m="test" label="Test" /><ModeBtn m="live" label="Live" />
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[11px] text-amber-500 flex items-center gap-1" data-testid="platform-warning"><AlertTriangle className="h-3.5 w-3.5" /> {WARN} The Live/Test switch above picks which set every payment uses.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {PLATFORM_FIELDS.map(([f, label, pref]) => (
            <div key={f}>
              <Label className="text-xs">{label} <span className="text-slate-500">({pref}…)</span></Label>
              {saved[f]
                ? <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-2" data-testid={`platform-saved-${f}`}>
                    •••• {saved[f].last4} · saved {new Date(saved[f].saved_at).toLocaleDateString()} by {saved[f].saved_by}
                    <button onClick={() => clearField(f)} className="text-red-400 hover:text-red-300" data-testid={`platform-clear-${f}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                : null}
              <Input className={`mt-1 h-9 ${inp}`} type="password" autoComplete="off" placeholder={saved[f] ? 'Replace…' : `${pref}…`}
                value={vals[f] || ''} onChange={e => setVals(s => ({ ...s, [f]: e.target.value }))} data-testid={`platform-input-${f}`} />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={save} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white h-9" data-testid="platform-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
          <Button onClick={testConn} disabled={busy} variant="outline" className="h-9 text-xs" data-testid="platform-test">Test connection</Button>
          {testRes && <span className={`text-xs ${testRes.ok ? 'text-green-500' : 'text-red-400'}`} data-testid="platform-test-result">
            {testRes.ok ? `OK · ${testRes.mode} · ${testRes.account_id}` : `Error (${testRes.mode || mode}): ${testRes.error}`}
          </span>}
        </div>
        <div className={`rounded-md border p-3 space-y-2 ${isDark ? 'border-slate-700 bg-slate-900/40' : 'border-slate-200 bg-slate-50'}`} data-testid="platform-setup-helper">
          <p className="text-xs font-medium">Stripe Dashboard setup</p>
          <CopyRow label="Webhook endpoint (use it for both webhooks)" value={setup.webhook_endpoint} testid="platform-webhook-endpoint" />
          <CopyRow label="OAuth redirect URI (own-Stripe orgs only)" value={setup.oauth_redirect_uri} testid="platform-redirect-uri" />
          <div className="text-[11px] text-slate-500">Your account events: <span className="font-mono">{(setup.webhook_events_account || []).join(', ')}</span></div>
          <div className="text-[11px] text-slate-500">Connected accounts events: <span className="font-mono">{(setup.webhook_events_connect || setup.webhook_events || []).join(', ')}</span></div>
          <ol className="list-decimal ml-4 text-[11px] text-slate-400 space-y-0.5">
            {(setup.checklist || []).map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </div>
      </CardContent>
    </Card>
  );
}

// Semprion HQ: Semprion Payments fee, methods, bank feeds + every org's setup.
export function HqPaymentsCard({ isDark, card, inp }) {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    axios.get(`${API}/api/stripe-connect/platform/hq`, { withCredentials: true })
      .then(r => { setD(r.data); setForm({ ...r.data.hq, fee_flat: (r.data.hq.fee_flat_cents || 0) / 100 }); })
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!d || !form) return null;
  const ro = !d.can_edit;
  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/api/stripe-connect/platform/hq`, {
        fee_percent: parseFloat(form.fee_percent || 0), fee_flat_cents: Math.round(parseFloat(form.fee_flat || 0) * 100),
        card_enabled: form.card_enabled, ach_enabled: form.ach_enabled, bank_feeds_enabled: form.bank_feeds_enabled,
      }, { withCredentials: true });
      toast.success('Semprion Payments settings saved'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };
  const Toggle = ({ k, label }) => (
    <label className={`text-xs flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
      <input type="checkbox" checked={!!form[k]} disabled={ro} onChange={e => setForm(f => ({ ...f, [k]: e.target.checked }))} data-testid={`hq-${k}`} /> {label}
    </label>
  );
  const PROC = Object.fromEntries(PROCESSORS.map(([k, l]) => [k, l.replace(' (recommended)', '')]));
  return (
    <Card className={card} data-testid="hq-payments-card">
      <CardHeader className="pb-2">
        <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
          <Landmark className="h-4 w-4 text-indigo-400" /> Semprion Payments
          <Badge variant="outline" className="text-[10px] uppercase">{d.mode}</Badge>
          <button onClick={load} className="ml-auto text-slate-400 hover:text-indigo-400" data-testid="hq-refresh"><RefreshCw className="h-4 w-4" /></button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className={`text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          Orgs on Semprion Payments get their own Stripe Express account under this platform; each payment goes
          straight to that org&apos;s bank, less the Semprion fee below. Stripe charges its own processing fee to
          Semprion&apos;s account on these payments, so set the fee to cover it. Subscriptions bill through the same Stripe account.
          {ro && ' Read-only — the Semprion HQ Super User edits these.'}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div><Label className="text-xs">Semprion fee (%)</Label>
            <Input type="number" step="0.1" min="0" max="30" className={`mt-1 h-9 ${inp}`} disabled={ro} value={form.fee_percent}
              onChange={e => setForm(f => ({ ...f, fee_percent: e.target.value }))} data-testid="hq-fee-percent" /></div>
          <div><Label className="text-xs">+ per payment ($)</Label>
            <Input type="number" step="0.01" min="0" className={`mt-1 h-9 ${inp}`} disabled={ro} value={form.fee_flat}
              onChange={e => setForm(f => ({ ...f, fee_flat: e.target.value }))} data-testid="hq-fee-flat" /></div>
          <div className="space-y-1"><Toggle k="card_enabled" label="Cards" /><Toggle k="ach_enabled" label="ACH bank debit" /></div>
          <div><Toggle k="bank_feeds_enabled" label="OL Finance bank links" /></div>
          {!ro && <Button onClick={save} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white h-9" data-testid="hq-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button>}
        </div>
        <div className={`rounded-md border max-h-64 overflow-auto ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          <table className="w-full text-xs" data-testid="hq-org-table">
            <thead className={isDark ? 'bg-slate-900/60 text-slate-400' : 'bg-slate-50 text-slate-500'}>
              <tr><th className="text-left px-2 py-1.5">Organization</th><th className="text-left px-2">Takes payments with</th><th className="text-left px-2">Account</th><th className="text-left px-2">Ready</th></tr>
            </thead>
            <tbody>
              {d.orgs.map(o => (
                <tr key={o.id} className={`border-t ${isDark ? 'border-slate-800 text-slate-300' : 'border-slate-100 text-slate-700'}`} data-testid={`hq-org-${o.id}`}>
                  <td className="px-2 py-1.5">{o.name}</td>
                  <td className="px-2">{PROC[o.processor] || o.processor}</td>
                  <td className="px-2 font-mono">{o.account_id || '—'}</td>
                  <td className="px-2">{o.ready ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> : <span className="text-amber-500">not set up</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// Semprion HQ: Square platform app keys (orgs then connect their own Square).
export function SquarePlatformCard({ isDark, card, inp }) {
  const [plat, setPlat] = useState(null);
  const [pmode, setPmode] = useState('sandbox');
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    axios.get(`${API}/api/square-connect/platform`, { withCredentials: true }).then(r => { setPlat(r.data); if (r.data.mode) setPmode(r.data.mode); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!plat?.can_edit) return null;
  const saved = (plat.fields || {})[pmode] || {};
  const savePlat = async () => {
    setBusy(true);
    try {
      const payload = { mode: pmode, [pmode]: {} };
      ['application_id', 'application_secret', 'webhook_signature_key'].forEach(f => { if ((vals[f] || '').trim()) payload[pmode][f] = vals[f].trim(); });
      await axios.put(`${API}/api/square-connect/platform`, payload, { withCredentials: true });
      toast.success('Square platform saved'); setVals({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };
  return (
    <Card className={card} data-testid="square-platform-card">
      <CardHeader className="pb-2"><CardTitle className={`text-base flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
        Square platform app (for orgs using their own Square)
        <span className="ml-auto flex gap-1">
          {['sandbox', 'production'].map(m => <button key={m} onClick={() => setPmode(m)} data-testid={`square-mode-${m}`}
            className={`px-2 py-1 text-xs rounded border ${pmode === m ? 'bg-indigo-600 text-white border-indigo-600' : (isDark ? 'border-slate-600 text-slate-300' : 'border-slate-300')}`}>{m}</button>)}
        </span></CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <p className="text-[11px] text-amber-500">These platform keys apply to every organization on OmniLeagues. Sandbox keys start with “sandbox-”.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[['application_id', 'Application ID'], ['application_secret', 'Application secret'], ['webhook_signature_key', 'Webhook signature key']].map(([f, label]) => (
            <div key={f}>
              <Label className="text-xs">{label}</Label>
              {saved[f] && <span className="text-[11px] text-slate-400 block" data-testid={`square-saved-${f}`}>•••• {saved[f].last4} · saved {new Date(saved[f].saved_at).toLocaleDateString()} by {saved[f].saved_by}</span>}
              <Input className={`mt-1 h-9 ${inp}`} type="password" autoComplete="off" placeholder={saved[f] ? 'Replace…' : ''} value={vals[f] || ''} onChange={e => setVals(s => ({ ...s, [f]: e.target.value }))} data-testid={`square-input-${f}`} />
            </div>
          ))}
        </div>
        <Button onClick={savePlat} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white h-9" data-testid="square-platform-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button>
      </CardContent>
    </Card>
  );
}

const Flag = ({ ok, label, testid }) => (
  <span className="inline-flex items-center gap-1 text-xs" data-testid={testid}>
    {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
    <span className={ok ? 'text-green-500' : 'text-amber-500'}>{label}</span>
  </span>
);

// Tenant: Semprion Payments (Express account under the HQ platform).
function SemprionPaymentsBody({ orgId, st, ro, isDark, reload }) {
  const [busy, setBusy] = useState(false);
  const ex = st.express || {};
  const back = window.location.origin + window.location.pathname;
  const onboard = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/stripe-connect/${orgId}/express/onboard`, { return_url: back });
      window.location.assign(data.url);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start setup'); setBusy(false); }
  };
  const dashboard = async () => {
    try { const { data } = await axios.post(`${API}/api/stripe-connect/${orgId}/express/dashboard`); window.open(data.url, '_blank', 'noopener'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not open the payouts dashboard'); }
  };
  if (!st.platform_configured) {
    return <p className="text-xs text-amber-500 flex items-center gap-1" data-testid="semprion-not-configured"><AlertTriangle className="h-4 w-4" />Semprion Payments is not switched on yet — Semprion HQ is finishing its setup.</p>;
  }
  const done = ex.account_id && ex.charges_enabled;
  return (
    <div className="space-y-3" data-testid="semprion-payments">
      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
        Payers pay through Semprion&apos;s Stripe; the money goes straight to this organization&apos;s bank account.
        Semprion fee per payment: <span className="font-medium" data-testid="semprion-fee">{feeText(st.hq)}</span>.
      </p>
      {ex.account_id ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className={`text-xs font-mono ${isDark ? 'text-slate-200' : 'text-slate-700'}`} data-testid="semprion-account">{ex.account_id}</span>
          <Flag ok={ex.details_submitted} label="Details submitted" testid="semprion-flag-details" />
          <Flag ok={ex.charges_enabled} label="Can take payments" testid="semprion-flag-charges" />
          <Flag ok={ex.payouts_enabled} label="Payouts to bank" testid="semprion-flag-payouts" />
          {ex.bank_last4 && <span className="text-xs text-slate-400" data-testid="semprion-bank">Bank •••• {ex.bank_last4}</span>}
          <span className="ml-auto flex gap-2">
            {!done && <Button onClick={onboard} disabled={ro || busy} className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 text-xs" data-testid="semprion-continue">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continue setup'}</Button>}
            {ex.details_submitted && <Button onClick={dashboard} disabled={ro} variant="outline" className="h-8 text-xs" data-testid="semprion-dashboard"><ExternalLink className="h-3.5 w-3.5 mr-1" />Payouts &amp; bank</Button>}
          </span>
        </div>
      ) : (
        <Button onClick={onboard} disabled={ro || busy} className="bg-indigo-600 hover:bg-indigo-700 text-white h-9" data-testid="semprion-setup">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Landmark className="h-4 w-4 mr-1" /> Set up Semprion Payments</>}
        </Button>
      )}
      {(ex.requirements_due || []).length > 0 && (
        <p className="text-xs text-amber-500" data-testid="semprion-requirements">Stripe still needs: {ex.requirements_due.join(', ').replace(/_/g, ' ')} — click Continue setup.</p>
      )}
      {!ex.account_id && <p className="text-[11px] text-slate-500">Stripe asks for the organization&apos;s legal name, EIN, a representative and the bank account for payouts. OmniLeagues never sees those details.</p>}
    </div>
  );
}

// Tenant: the org's OWN Stripe account (OAuth).
function OwnStripeBody({ orgId, st, ro, isDark, reload }) {
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/stripe-connect/${orgId}/oauth-url`, { redirect_uri: window.location.origin + window.location.pathname });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start Stripe connect'); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm('Disconnect this Stripe account? New payments will stop until you reconnect.')) return;
    setBusy(true);
    try { await axios.post(`${API}/api/stripe-connect/${orgId}/disconnect`); toast.success('Disconnected'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Disconnect failed'); }
    finally { setBusy(false); }
  };
  if (!st.oauth_configured) {
    return <p className="text-xs text-amber-500 flex items-center gap-1" data-testid="payments-platform-missing"><AlertTriangle className="h-4 w-4" />Connecting your own Stripe account is not switched on yet — ask Semprion HQ.</p>;
  }
  if (!st.connected) {
    return (
      <div className="flex items-center gap-3">
        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>No Stripe account connected.</p>
        <Button onClick={connect} disabled={ro || busy} className="bg-indigo-600 hover:bg-indigo-700 text-white h-9" data-testid="payments-connect">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2 className="h-4 w-4 mr-1" /> Connect with Stripe</>}
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-4">
        <span className={`text-sm font-mono ${isDark ? 'text-white' : 'text-slate-900'}`} data-testid="payments-account-id">{st.account_id}</span>
        <Flag ok={st.charges_enabled} label="Charges" testid="flag-charges" />
        <Flag ok={st.payouts_enabled} label="Payouts" testid="flag-payouts" />
        <Flag ok={st.details_submitted} label="Details submitted" testid="flag-details" />
        <span className="text-xs text-slate-400" data-testid="payments-bank">{st.bank_last4 ? `Bank •••• ${st.bank_last4}` : ''}</span>
        <div className="ml-auto flex gap-2">
          {st.dashboard_url && <a href={st.dashboard_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-400 hover:underline" data-testid="payments-dashboard"><ExternalLink className="h-3.5 w-3.5" /> Stripe dashboard</a>}
          <Button onClick={connect} disabled={ro || busy} variant="outline" className="h-8 text-xs" data-testid="payments-reconnect"><Link2 className="h-3.5 w-3.5 mr-1" /> Reconnect</Button>
          <Button onClick={disconnect} disabled={ro || busy} variant="outline" className="h-8 text-xs text-red-400" data-testid="payments-disconnect"><Unlink className="h-3.5 w-3.5 mr-1" /> Disconnect</Button>
        </div>
      </div>
      {(st.requirements_due || []).length > 0 && (
        <p className="text-xs text-amber-500" data-testid="payments-requirements">Stripe needs more info: {st.requirements_due.join(', ')}</p>
      )}
    </div>
  );
}

// Tenant: the org's OWN Square account.
function OwnSquareBody({ orgId, ro, isDark, inp }) {
  const [st, setSt] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/api/square-connect/${orgId}/status`, { withCredentials: true }).then(r => setSt(r.data)).catch(() => {});
  }, [orgId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('provider') === 'square' && p.get('code')) {
      axios.post(`${API}/api/square-connect/${orgId}/connect`,
        { code: p.get('code'), state: p.get('state'), redirect_uri: window.location.origin + window.location.pathname + '?provider=square' },
        { withCredentials: true })
        .then(() => { toast.success('Square connected'); window.history.replaceState({}, '', window.location.pathname); load(); })
        .catch(e => toast.error(e.response?.data?.detail || 'Square connect failed'));
    }
  }, [orgId, load]);
  if (!st) return null;
  const connect = async () => {
    try {
      const { data } = await axios.post(`${API}/api/square-connect/${orgId}/oauth-url`,
        { redirect_uri: window.location.origin + window.location.pathname + '?provider=square' }, { withCredentials: true });
      window.location.assign(data.url);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start Square connect'); }
  };
  const disconnect = async () => {
    if (!window.confirm('Disconnect Square for this organization?')) return;
    try { await axios.post(`${API}/api/square-connect/${orgId}/disconnect`, {}, { withCredentials: true }); toast.success('Square disconnected'); load(); }
    catch (e) { toast.error('Disconnect failed'); }
  };
  const setLocation = async (locId) => {
    try { await axios.put(`${API}/api/square-connect/${orgId}/location`, { location_id: locId }, { withCredentials: true }); toast.success('Location saved'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to save location'); }
  };
  if (!st.connected) {
    return st.platform_configured
      ? (ro ? <p className="text-sm text-slate-400" data-testid="square-not-connected-ro">No Square account connected.</p>
            : <Button className="bg-indigo-600 hover:bg-indigo-700 text-white h-9" onClick={connect} data-testid="square-connect"><Link2 className="h-4 w-4 mr-1" />Connect with Square</Button>)
      : <p className="text-xs text-amber-500 flex items-center gap-1"><AlertTriangle className="h-4 w-4" />Square is not switched on yet — ask Semprion HQ.</p>;
  }
  return (
    <div className="space-y-2" data-testid="square-connected">
      <div className="flex items-center gap-3 text-sm">
        <CheckCircle2 className="h-4 w-4 text-green-500" />
        <span className={isDark ? 'text-slate-200' : 'text-slate-700'}>{st.merchant_name || 'Connected'} · {st.location_name || (st.location_id || 'No location selected')} · {st.environment}</span>
        {!ro && <Button variant="outline" className="h-8 text-xs ml-auto" onClick={disconnect} data-testid="square-disconnect"><Unlink className="h-3.5 w-3.5 mr-1" />Disconnect</Button>}
      </div>
      {!ro && (st.locations || []).length > 0 && (
        <div className="flex items-center gap-2">
          <Label className="text-xs">Checkout location</Label>
          <Select value={st.location_id || undefined} onValueChange={setLocation}>
            <SelectTrigger className={`w-64 h-9 ${inp}`} data-testid="square-location-select"><SelectValue placeholder="Select a location" /></SelectTrigger>
            <SelectContent>{(st.locations || []).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {!st.location_id && <p className="text-xs text-amber-500 flex items-center gap-1" data-testid="square-no-location"><AlertTriangle className="h-4 w-4" />Select a checkout location to accept Square payments.</p>}
      <p className="text-[11px] text-slate-500">Square checkout offers the methods turned on in your Square dashboard (card, Apple Pay, Cash App Pay, ACH where Square allows it).</p>
    </div>
  );
}

export function PaymentsSettings({ orgId, isDark = true }) {
  const [st, setSt] = useState(null);
  const card = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const inp = isDark ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300';

  const load = useCallback(() => {
    axios.get(`${API}/api/stripe-connect/${orgId}/status`)
      .then(r => setSt(r.data)).catch(() => toast.error('Could not load payment settings'));
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  // Returns from Stripe: ?code&state = own-Stripe OAuth; ?express=… = Express signup.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const code = q.get('code'), state = q.get('state');
    if (code && state && q.get('provider') !== 'square') {
      axios.post(`${API}/api/stripe-connect/${orgId}/connect`, { code, state })
        .then(() => { toast.success('Stripe account connected'); window.history.replaceState({}, '', window.location.pathname); load(); })
        .catch(e => toast.error(e.response?.data?.detail || 'Stripe connection failed'));
    } else if (q.get('express')) {
      if (q.get('express') === 'return') toast.success('Back from Stripe — status refreshed');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [orgId, load]);

  if (!st) return <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>;

  const ro = !st.can_edit;
  const proc = st.processor || 'semprion';
  const pm = st.payment_methods || { card: true, ach: true };
  const setProcessor = async (v) => {
    try { await axios.put(`${API}/api/square-connect/${orgId}/processor`, { processor: v }, { withCredentials: true }); toast.success('Saved'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const setMethod = async (k, v) => {
    try { await axios.put(`${API}/api/stripe-connect/${orgId}/methods`, { [k]: v }); toast.success('Saved'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const saveSettings = async (patch) => {
    try { await axios.put(`${API}/api/stripe-connect/${orgId}/settings`, patch); toast.success('Saved'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const stripeRoute = proc === 'semprion' || proc === 'stripe';

  return (
    <div className="space-y-4" data-testid="payments-settings">
      <Card className={card}>
        <CardHeader className="pb-2">
          <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`} data-testid="stripe-tenant-title">
            <CreditCard className="h-5 w-5 text-indigo-400" /> {st.org_name || 'This organization'} — taking payments
            {st.test_mode && <Badge variant="outline" className="ml-2 text-[10px]">TEST MODE</Badge>}
            <button onClick={load} className="ml-auto text-slate-400 hover:text-indigo-400" data-testid="payments-refresh"><RefreshCw className="h-4 w-4" /></button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {ro && <p className="text-[11px] text-amber-500" data-testid="payments-readonly">Read-only — payment setup is managed by the Tech Specialist.</p>}
          <div className="flex flex-wrap items-center gap-3">
            <Label className="text-sm">Payments go through</Label>
            <Select value={proc} onValueChange={setProcessor} disabled={ro}>
              <SelectTrigger className={`w-72 h-9 ${inp}`} data-testid="processor-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PROCESSORS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {proc === 'semprion' && <SemprionPaymentsBody orgId={orgId} st={st} ro={ro} isDark={isDark} reload={load} />}
          {proc === 'stripe' && <OwnStripeBody orgId={orgId} st={st} ro={ro} isDark={isDark} reload={load} />}
          {proc === 'square' && <OwnSquareBody orgId={orgId} ro={ro} isDark={isDark} inp={inp} />}

          {stripeRoute && (
            <div className={`flex flex-wrap items-center gap-4 pt-3 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`} data-testid="payments-methods">
              <span className="text-xs font-medium">Payers can pay by</span>
              {[['card', 'Card', st.hq?.card_enabled], ['ach', 'ACH bank transfer (clears in ~4 business days)', st.hq?.ach_enabled]].map(([k, label, allowed]) => (
                <label key={k} className={`text-xs flex items-center gap-2 ${allowed ? (isDark ? 'text-slate-300' : 'text-slate-700') : 'text-slate-500'}`}>
                  <input type="checkbox" checked={!!(allowed && pm[k])} disabled={ro || !allowed} onChange={e => setMethod(k, e.target.checked)} data-testid={`payments-method-${k}`} />
                  {label}{!allowed && ' — off for all orgs (Semprion HQ)'}
                </label>
              ))}
            </div>
          )}

          <div className={`grid grid-cols-2 md:grid-cols-5 gap-3 pt-3 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
            <div>
              <Label className="text-xs">Currency</Label>
              <Input className={`mt-1 h-9 ${inp}`} defaultValue={st.currency} disabled={ro}
                onBlur={e => e.target.value.toLowerCase() !== st.currency && saveSettings({ currency: e.target.value })} data-testid="payments-currency" />
            </div>
            <div>
              <Label className="text-xs">Statement descriptor</Label>
              <Input className={`mt-1 h-9 ${inp}`} defaultValue={st.statement_descriptor} maxLength={22} disabled={ro}
                onBlur={e => e.target.value !== st.statement_descriptor && saveSettings({ statement_descriptor: e.target.value })} data-testid="payments-descriptor" />
            </div>
            <div>
              <Label className="text-xs">Online-fee rate (%)</Label>
              <Input type="number" step="0.1" min="0" className={`mt-1 h-9 ${inp}`} defaultValue={st.online_fee_rate} disabled={ro}
                onBlur={e => parseFloat(e.target.value || 0) !== st.online_fee_rate && saveSettings({ online_fee_rate: parseFloat(e.target.value || 0) })} data-testid="payments-fee-rate" />
            </div>
            <div>
              <Label className="text-xs">Online-fee fixed ($)</Label>
              <Input type="number" step="0.01" min="0" className={`mt-1 h-9 ${inp}`} defaultValue={st.online_fee_flat || 0} disabled={ro}
                onBlur={e => parseFloat(e.target.value || 0) !== (st.online_fee_flat || 0) && saveSettings({ online_fee_flat: parseFloat(e.target.value || 0) })} data-testid="payments-fee-flat" />
            </div>
            <div className="flex items-end">
              <label className={`text-xs flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <input type="checkbox" defaultChecked={st.pass_online_fee} disabled={ro}
                  onChange={e => saveSettings({ pass_online_fee: e.target.checked })} data-testid="payments-pass-fee" />
                Pass online fee to payer
              </label>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default PaymentsSettings;
